import { useCallback, useEffect, useState } from "react";
import styled from "styled-components";

import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import StopIcon from "@mui/icons-material/Stop";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Skeleton from "@mui/material/Skeleton";
import { useAppDispatch, useAppSelector } from "model/rootReducer";

import { HintPopper, PageTitleBar } from "common/components";
import { AnimalsIcon, FieldsIcon, HectaresIcon } from "common/icons";
import { useTranslation } from "common/locales";
import { dryMatterToColor } from "common/utils/grazing-logic";
import {
  collapseMobileBottomSheet,
  expandMobileBottomSheet,
  getAllArableFieldGeoms,
  getAllFieldGeoms,
  getTotalAvgDM,
  getTotalFarmAnimalCount,
  getTotalFarmArea,
  getTotalFieldCount,
  selectCurrentFarm,
  setIsEditing,
  setZoomToGeom,
} from "model";
import { useHistory } from "react-router-dom";
import AnimalGroupCards from "./AnimalGroupCards";
import FarmSwitch from "./FarmSwitch";
import FieldTypeCards from "./FieldTypeCards";

import NoFieldsModal from "features/fieldEdit/NoFieldsModal";
import { mixpanel } from "../../common/analytics";

export default function FarmView() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const iconSize = isMobile ? "small" : "medium";
  const [showNoFieldsModal, setShowNoFieldsModal] = useState(false);

  const farmDryMatterNow = useAppSelector(getTotalAvgDM);
  const totalAnimals = useAppSelector(getTotalFarmAnimalCount);
  const totalFieldCount = useAppSelector(getTotalFieldCount);
  const totalHectares = useAppSelector(getTotalFarmArea);
  const [hintContainer, setHintContainer] = useState<HTMLDivElement | null>(
    null
  );
  const user = useAppSelector((state) => state.user.details);
  const isCorporateUser = !!(
    user?.corporations?.length && user.corporations.length > 0
  );
  const { t } = useTranslation();

  // Drag state for mobile bottom sheet
  const isExpanded = useAppSelector(
    (state) => state.app.mobileBottomSheetExpanded
  );
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [startY, setStartY] = useState(0);
  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(
    null
  );
  const [isAtScrollTop, setIsAtScrollTop] = useState(true);
  const [lockedScrollTop, setLockedScrollTop] = useState<number | null>(null);
  const [bottomSheetRef, setBottomSheetRef] = useState<HTMLDivElement | null>(
    null
  );
  const [handleRef, setHandleRef] = useState<HTMLDivElement | null>(null);

  const dispatch = useAppDispatch();
  const farm = useAppSelector(selectCurrentFarm);
  const loading = useAppSelector((state) => state.app.loading === "pending");
  const fieldLoadStatus = useAppSelector((state) => state.fields.status);
  const growthRate = useAppSelector((state) => state.app.growthRate);
  const fieldGeoms = useAppSelector(getAllFieldGeoms);
  const arableFieldGeoms = useAppSelector(getAllArableFieldGeoms);
  const fieldsLoading = useAppSelector(
    (state) => state.fields.loading === "pending"
  );
  const arableFieldsLoading = useAppSelector(
    (state) => state.arableFields.loading === "pending"
  );
  const farmsLoading = useAppSelector(
    (state) => state.farms.loading === "pending"
  );
  const drawerOpen = useAppSelector((state) => state.app.drawerOpen);
  const history = useHistory();
  // Update showNoFieldsModal when field status changes
  useEffect(() => {
    // Only show the modal if we're certain there are no fields
    // This requires that the farm data has been loaded and we're not in any loading state
    const shouldShowModal = Boolean(
      !loading &&
        !farmsLoading &&
        !fieldsLoading &&
        !arableFieldsLoading &&
        farm?.guid &&
        farm?.fields.length === 0 &&
        fieldGeoms.features.length === 0 &&
        arableFieldGeoms.features.length === 0
    );

    setShowNoFieldsModal(shouldShowModal);
  }, [
    loading,
    farmsLoading,
    fieldsLoading,
    arableFieldsLoading,
    farm?.guid,
    farm?.fields?.length,
    fieldGeoms.features.length,
    arableFieldGeoms.features.length,
  ]);

  // Drag handlers (native event handlers)
  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      const isHandle = target.closest('[data-handle="true"]');

      // Always allow drag from handle
      if (isHandle) {
        setIsDragging(true);
        setStartY(e.touches[0].clientY);
        // Lock scroll position when starting drag
        if (scrollContainer) {
          setLockedScrollTop(scrollContainer.scrollTop);
        }
        return;
      }

      // When collapsed, allow drag gestures everywhere
      if (!isExpanded) {
        setIsDragging(true);
        setStartY(e.touches[0].clientY);
        // Lock scroll position when starting drag
        if (scrollContainer) {
          setLockedScrollTop(scrollContainer.scrollTop);
        }
        return;
      }

      // When expanded, only start drag detection if we're at the top of scroll
      if (isExpanded && isAtScrollTop) {
        // Just store the start position, don't set dragging yet
        setStartY(e.touches[0].clientY);
        if (scrollContainer) {
          setLockedScrollTop(scrollContainer.scrollTop);
        }
      }
    },
    [isExpanded, scrollContainer, isAtScrollTop]
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      const currentY = e.touches[0].clientY;
      const deltaY = currentY - startY;

      // If we're not dragging yet, check if we should start
      if (!isDragging) {
        // Only consider starting drag when expanded and at top, and moving down
        if (isExpanded && isAtScrollTop && deltaY > 10) {
          // User is trying to drag down from top - start dragging
          setIsDragging(true);
          setDragOffset(deltaY);
          // Only prevent default if we can - if not, the drag will still work but might conflict with scroll
          if (e.cancelable) {
            e.preventDefault();
          }
        }
        // Otherwise, let normal scroll behavior handle it
        return;
      }

      // Now handle active dragging

      // When collapsed, only allow drag up
      if (!isExpanded) {
        if (deltaY < 0) {
          setDragOffset(deltaY);
          // Prevent default scrolling when dragging up from collapsed state
          if (e.cancelable) {
            e.preventDefault();
          }
        }
        return;
      }

      // When expanded, handle drag down logic
      if (isExpanded && deltaY > 0) {
        // Use the locked scroll position to determine if we were at top
        const wasAtTop = (lockedScrollTop || 0) <= 5;

        if (wasAtTop) {
          // Allow drag down when we started at top
          setDragOffset(deltaY);
          // Try to prevent scrolling to allow smooth dragging
          if (e.cancelable) {
            e.preventDefault();
          }
        } else {
          // Stop dragging to allow normal scrolling
          setIsDragging(false);
          setDragOffset(0);
          setLockedScrollTop(null);
          // Restore scroll position
          if (scrollContainer && lockedScrollTop !== null) {
            scrollContainer.scrollTop = lockedScrollTop;
          }
        }
      }
    },
    [
      isDragging,
      startY,
      isExpanded,
      scrollContainer,
      lockedScrollTop,
      isAtScrollTop,
    ]
  );

  const handleTouchEnd = useCallback(() => {
    // Always clean up, even if we weren't actively dragging
    setIsDragging(false);
    setStartY(0);
    setLockedScrollTop(null);

    // Only apply snap logic if we were actually dragging
    if (!isDragging) {
      setDragOffset(0);
      return;
    }

    // Snap logic based on drag distance
    const snapThreshold = 100; // pixels

    if (!isExpanded && dragOffset < -snapThreshold) {
      // Dragged up enough when collapsed - expand
      dispatch(expandMobileBottomSheet());
    } else if (isExpanded && dragOffset > snapThreshold) {
      // Dragged down enough when expanded - collapse
      dispatch(collapseMobileBottomSheet());
      // Reset scroll position when collapsing
      if (scrollContainer) {
        scrollContainer.scrollTop = 0;
      }
    }

    // Reset drag offset with animation
    setDragOffset(0);
  }, [isDragging, dragOffset, isExpanded, dispatch, scrollContainer]);

  // Register native touch event listeners with { passive: false }
  useEffect(() => {
    if (!bottomSheetRef || !handleRef) return;

    // Always attach non-passive listeners to the handle for reliable drag detection
    handleRef.addEventListener("touchstart", handleTouchStart, {
      passive: false,
    });
    handleRef.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
    handleRef.addEventListener("touchend", handleTouchEnd, {
      passive: false,
    });

    // Attach listeners to the bottom sheet for collapsed state (when entire sheet is draggable)
    bottomSheetRef.addEventListener("touchstart", handleTouchStart, {
      passive: false,
    });
    bottomSheetRef.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
    bottomSheetRef.addEventListener("touchend", handleTouchEnd, {
      passive: false,
    });

    return () => {
      handleRef.removeEventListener("touchstart", handleTouchStart);
      handleRef.removeEventListener("touchmove", handleTouchMove);
      handleRef.removeEventListener("touchend", handleTouchEnd);
      bottomSheetRef.removeEventListener("touchstart", handleTouchStart);
      bottomSheetRef.removeEventListener("touchmove", handleTouchMove);
      bottomSheetRef.removeEventListener("touchend", handleTouchEnd);
    };
  }, [
    bottomSheetRef,
    handleRef,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  ]);

  // Track scroll position
  useEffect(() => {
    if (!scrollContainer) return;

    const handleScroll = () => {
      // Don't update scroll position tracking when locked
      if (lockedScrollTop !== null) return;

      const isAtTop = scrollContainer.scrollTop <= 5; // Allow small tolerance
      setIsAtScrollTop(isAtTop);
    };

    // Set initial state
    const initialIsAtTop = scrollContainer.scrollTop <= 5;
    setIsAtScrollTop(initialIsAtTop);

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, [scrollContainer, lockedScrollTop]);

  // Reset scroll position when collapsing
  useEffect(() => {
    if (!isExpanded && scrollContainer) {
      scrollContainer.scrollTop = 0;
    }
  }, [isExpanded, scrollContainer]);

  useEffect(() => {
    if (fieldGeoms.features.length > 0) {
      dispatch(setZoomToGeom(fieldGeoms));
    } else if (arableFieldGeoms.features.length > 0) {
      dispatch(setZoomToGeom(arableFieldGeoms));
    }
    if (farm?.geom) {
      dispatch(setZoomToGeom(farm.geom));
    }
    // we need to disable lint here because we cannot have dependency on an object - it would keep zooming otherwise
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farm?.guid, dispatch]);

  const fieldHintOpen =
    fieldLoadStatus === "success" &&
    fieldGeoms.features.length === 0 &&
    arableFieldGeoms.features.length === 0;

  const animalGroupHintOpen = false;
  const hintOpen =
    (fieldHintOpen || animalGroupHintOpen) &&
    Boolean(hintContainer) &&
    drawerOpen;

  const handleAddMoreFields = () => {
    mixpanel.track("Add more fields clicked");
    dispatch(setIsEditing());
    history.push(`/add-field/enter-details`);
  };

  // Mobile bottom sheet layout
  if (isMobile) {
    return (
      <>
        {/* Loading overlay when switching farms */}
        {loading && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "#F7FCF1",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box sx={{ textAlign: "center", color: "#656565" }}>
              <CircularProgress size={40} sx={{ color: "656565" }} />
              <Typography
                variant="h6"
                sx={{ mb: 1, fontWeight: 600, color: "#656565" }}
              >
                {t("farmView.loading.title")}
              </Typography>
              <Typography
                variant="h6"
                sx={{ mb: 1, color: "#656565", fontWeight: 600 }}
              >
                {farm?.name}
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "#656565", maxWidth: 280 }}
              >
                {t("farmView.loading.description")}
              </Typography>
            </Box>
          </Box>
        )}

        {/* FarmSwitch positioned at top of map */}
        <Box
          sx={{
            position: "fixed",
            top: 20,
            left: 20,
            right: 20,
            zIndex: 1000,
            pointerEvents: "auto",
          }}
        >
          <FarmSwitch
            switchToFarmView={true}
            fontVariant={"h6"}
            fontWeight={600}
            fontColor={"black"}
          />
        </Box>

        <MobileBottomSheetContainer>
          <NoFieldsModal
            open={showNoFieldsModal}
            onClose={() => setShowNoFieldsModal(false)}
          />
          <MobileBottomSheet
            ref={setBottomSheetRef}
            isExpanded={isExpanded}
            isDragging={isDragging}
            dragOffset={dragOffset}
          >
            <BottomSheetContent
              isExpanded={isExpanded}
              ref={setScrollContainer}
              data-scrollable="true"
              style={{
                overflowY: isDragging
                  ? "hidden"
                  : isExpanded
                  ? "auto"
                  : "hidden",
                touchAction: isDragging ? "none" : "auto",
              }}
            >
              <HandleContainer>
                <BottomSheetHandle ref={setHandleRef} data-handle="true" />
              </HandleContainer>
              <Container data-scrollable="false">
                <div />
              </Container>
              {loading && (
                <FarmTotals data-scrollable="false">
                  <List dense disablePadding>
                    <ListItem>
                      <FarmSwitchContainer>
                        <Skeleton variant="text" width={100} />
                      </FarmSwitchContainer>
                      <ListItemSecondaryAction>
                        <Skeleton
                          variant="rectangular"
                          width={48}
                          height={24}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    {[1, 2, 3, 4, 5, 6].map((n) => (
                      <ListItem key={`farm-total-skeleton-${n}`}>
                        <ListItemIcon>
                          <Skeleton
                            variant="rectangular"
                            width={24}
                            height={24}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={<Skeleton variant="text" width={110} />}
                          secondary={<Skeleton variant="text" width={130} />}
                        />
                      </ListItem>
                    ))}
                  </List>
                </FarmTotals>
              )}
              {!loading && (
                <FarmTotals data-scrollable="false">
                  <List dense disablePadding>
                    <ListItem>
                      <ListItemText>
                        <Typography variant="h6" fontWeight={600}>
                          {farm?.name}
                        </Typography>
                      </ListItemText>
                      <ListItemSecondaryAction>
                        <div ref={setHintContainer}></div>
                        <HintPopper
                          open={hintOpen}
                          title={t(
                            fieldHintOpen
                              ? "hint.welcome.title"
                              : "hint.animalGroups.title"
                          )}
                          text={t(
                            fieldHintOpen
                              ? "hint.welcome.text"
                              : "hint.animalGroups.text"
                          )}
                          parentEl={hintContainer}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Box sx={{ display: "flex", flexDirection: "row" }}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          width: "100%",
                          justifyContent: "space-between",
                          alignItems: "center",
                          px: 2,
                          py: 1,
                        }}
                      >
                        {totalAnimals !== undefined ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flex: 1,
                              justifyContent: "center",
                              gap: 1,
                              fontSize: { xs: "0.875rem", sm: "1rem" },
                            }}
                          >
                            <AnimalsIcon fontSize={iconSize} />
                            <span>{totalAnimals} Animals</span>
                          </Box>
                        ) : null}
                        {totalHectares ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flex: 1,
                              justifyContent: "center",
                              gap: 1,
                              fontSize: { xs: "0.875rem", sm: "1rem" },
                            }}
                          >
                            <HectaresIcon fontSize={iconSize} />
                            <span>
                              {Math.round(totalHectares * 100) / 100} Ha
                            </span>
                          </Box>
                        ) : null}
                        {totalFieldCount ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flex: 1,
                              justifyContent: "center",
                              gap: 1,
                              fontSize: { xs: "0.875rem", sm: "1rem" },
                            }}
                          >
                            <FieldsIcon fontSize={iconSize} />
                            <span>{totalFieldCount} Fields</span>
                          </Box>
                        ) : null}
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "row" }}>
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        {farmDryMatterNow ? (
                          <ListItem>
                            <ListItemIcon>
                              <StopIcon
                                htmlColor={dryMatterToColor(
                                  farmDryMatterNow,
                                  "#1C4321"
                                )}
                                sx={{
                                  color: dryMatterToColor(
                                    farmDryMatterNow,
                                    "#1C4321"
                                  ),
                                  "& path": {
                                    fill: `${dryMatterToColor(
                                      farmDryMatterNow,
                                      "#1C4321"
                                    )} !important`,
                                  },
                                }}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={t("farm.totalDM.label")}
                              secondary={`${farmDryMatterNow} ${t(
                                "farm.totalDM.unit"
                              )}`}
                            />
                          </ListItem>
                        ) : null}
                        {growthRate ? (
                          <ListItem>
                            <ListItemIcon>
                              <ArrowUpwardIcon />
                            </ListItemIcon>
                            <ListItemText
                              primary={t("farm.growth.label")}
                              secondary={`${
                                growthRate ? Math.round(growthRate) : ""
                              } ${t("farm.growth.unit")}`}
                            />
                          </ListItem>
                        ) : null}
                      </Box>
                    </Box>
                  </List>
                </FarmTotals>
              )}
              <Box
                sx={{
                  backgroundColor: "white",
                  margin: "20px 0px",
                  px: 3,
                  pt: 2,
                }}
              >
                {!isCorporateUser && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: 2,
                    }}
                  >
                    <Typography variant="body1" fontWeight="bold">
                      {t("farm.livestock.label")}
                    </Typography>
                    <AddMoreButton
                      variant="text"
                      onClick={() => {
                        mixpanel.track("Add animal group clicked");
                        history.push("/add-animal-group");
                      }}
                    >
                      {t("animalGroupTypeCards.addMoreAnimalGroups")}
                    </AddMoreButton>
                  </Box>
                )}
                <AnimalGroupCards />
              </Box>
              <Box
                sx={{
                  backgroundColor: "white",
                  margin: "20px 0px",
                  px: 3,
                  pt: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 2,
                  }}
                >
                  <Typography variant="body1" fontWeight="bold">
                    {t("farm.fields.label")}
                  </Typography>
                  {!isCorporateUser && (
                    <AddMoreButton variant="text" onClick={handleAddMoreFields}>
                      {t("fieldTypeCards.addMoreFields")}
                    </AddMoreButton>
                  )}
                </Box>
                <FieldTypeCards />
              </Box>
            </BottomSheetContent>
          </MobileBottomSheet>
        </MobileBottomSheetContainer>
      </>
    );
  }

  // Desktop layout (unchanged)
  return (
    <>
      <NoFieldsModal
        open={showNoFieldsModal}
        onClose={() => setShowNoFieldsModal(false)}
      />

      <PageTitleBar title={farm?.name ?? ""} showDivider />

      <Box
        sx={{
          backgroundColor: "#F7FCF1",
          minHeight: "100vh",
          position: "relative",
          paddingBottom: "20px",
        }}
      >
        {/* Loading overlay when switching farms */}
        {loading && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "#F7FCF1",
              zIndex: theme.zIndex.modal + 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box sx={{ textAlign: "center", color: "#656565" }}>
              <CircularProgress size={40} sx={{ color: "656565" }} />
              <Typography
                variant="h6"
                sx={{ mb: 1, color: "#656565", fontWeight: 600 }}
              >
                {t("farmView.loading.title")}
              </Typography>
              <Typography
                variant="h6"
                sx={{ mb: 1, color: "#656565", fontWeight: 600 }}
              >
                {farm?.name}
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "#656565", maxWidth: 280 }}
              >
                {t("farmView.loading.description")}
              </Typography>
            </Box>
          </Box>
        )}

        <Container>
          <div />
        </Container>
        {loading && (
          <FarmTotals>
            <List dense disablePadding>
              <ListItem>
                <FarmSwitchContainer>
                  <Skeleton variant="text" width={100} />
                </FarmSwitchContainer>
                <ListItemSecondaryAction>
                  <Skeleton variant="rectangular" width={48} height={24} />
                </ListItemSecondaryAction>
              </ListItem>
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <ListItem key={`farm-total-skeleton-${n}`}>
                  <ListItemIcon>
                    <Skeleton variant="rectangular" width={24} height={24} />
                  </ListItemIcon>
                  <ListItemText
                    primary={<Skeleton variant="text" width={110} />}
                    secondary={<Skeleton variant="text" width={130} />}
                  />
                </ListItem>
              ))}
            </List>
          </FarmTotals>
        )}
        {!loading && (
          <FarmTotals>
            <List dense disablePadding>
              {/* FarmSwitch for desktop - inline below Home button */}
              {!isMobile && (
                <ListItem sx={{ py: 2 }}>
                  <Box sx={{ width: "100%" }}>
                    <FarmSwitch
                      switchToFarmView={true}
                      fontVariant={"h6"}
                      fontWeight={600}
                      fontColor={"black"}
                    />
                  </Box>
                </ListItem>
              )}

              <Box sx={{ display: "flex", flexDirection: "row" }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    width: "100%",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 2,
                    py: 1,
                  }}
                >
                  {totalAnimals !== undefined ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flex: 1,
                        justifyContent: "center",
                        gap: 1,
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                      }}
                    >
                      <AnimalsIcon fontSize={iconSize} />
                      <span>{totalAnimals} Animals</span>
                    </Box>
                  ) : null}
                  {totalHectares ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flex: 1,
                        justifyContent: "center",
                        gap: 1,
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                      }}
                    >
                      <HectaresIcon fontSize={iconSize} />
                      <span>{Math.round(totalHectares * 100) / 100} Ha</span>
                    </Box>
                  ) : null}
                  {totalFieldCount ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flex: 1,
                        justifyContent: "center",
                        gap: 1,
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                      }}
                    >
                      <FieldsIcon fontSize={iconSize} />
                      <span>{totalFieldCount} Fields</span>
                    </Box>
                  ) : null}
                </Box>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "row" }}>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {farmDryMatterNow ? (
                    <ListItem>
                      <ListItemIcon>
                        <StopIcon
                          htmlColor={dryMatterToColor(
                            farmDryMatterNow,
                            "#1C4321"
                          )}
                          sx={{
                            color: dryMatterToColor(
                              farmDryMatterNow,
                              "#1C4321"
                            ),
                            "& path": {
                              fill: `${dryMatterToColor(
                                farmDryMatterNow,
                                "#1C4321"
                              )} !important`,
                            },
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={t("farm.totalDM.label")}
                        secondary={`${farmDryMatterNow} ${t(
                          "farm.totalDM.unit"
                        )}`}
                      />
                    </ListItem>
                  ) : null}
                  {growthRate ? (
                    <ListItem>
                      <ListItemIcon>
                        <ArrowUpwardIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={t("farm.growth.label")}
                        secondary={`${
                          growthRate ? Math.round(growthRate) : ""
                        } ${t("farm.growth.unit")}`}
                      />
                    </ListItem>
                  ) : null}
                </Box>
              </Box>
            </List>
          </FarmTotals>
        )}
        <Container
          style={{
            backgroundColor: "white",
            margin: "20px 0px",
            padding: "20px 40px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 2,
            }}
          >
            <Typography variant="body1" fontWeight="bold">
              {t("farm.livestock.label")}
            </Typography>
            <AddMoreButton
              variant="text"
              onClick={() => {
                mixpanel.track("Add animal group clicked");
                history.push("/add-animal-group");
              }}
            >
              {t("animalGroupTypeCards.addMoreAnimalGroups")}
            </AddMoreButton>
          </Box>
          <AnimalGroupCards />
        </Container>
        <Container
          style={{
            backgroundColor: "white",
            margin: "20px 0px 0px 0px",
            padding: "20px 40px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 2,
            }}
          >
            <Typography variant="body1" fontWeight="bold">
              {t("farm.fields.label")}
            </Typography>
            <AddMoreButton variant="text" onClick={handleAddMoreFields}>
              {t("fieldTypeCards.addMoreFields")}
            </AddMoreButton>
          </Box>
          <FieldTypeCards />
        </Container>
      </Box>
    </>
  );
}

const FarmTotals = styled(Container)(
  ({ theme }) => `
  background-color: white;
  padding-bottom: ${theme.spacing(2)};
`
);

const FarmSwitchContainer = styled(ListItemText)`
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

const MobileBottomSheetContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100vh;
  pointer-events: none;
  z-index: 1000;
`;

const MobileBottomSheet = styled.div<{
  isExpanded: boolean;
  isDragging: boolean;
  dragOffset: number;
}>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: #f7fcf1;
  border-radius: 20px 20px 0 0;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.1);
  height: 100vh;
  transform: translateY(
    ${({ dragOffset, isExpanded }) => {
      const collapsedPosition = 70; // 50vh hidden, 50vh visible (increased from 25vh)
      const expandedPosition = 0; // 0vh hidden, 100vh visible (full screen)

      const basePosition = isExpanded ? expandedPosition : collapsedPosition;
      const dragPercentage = (Number(dragOffset) / window.innerHeight) * 100;

      // Apply drag offset
      if (!isExpanded && dragOffset < 0) {
        // Dragging up from collapsed state
        return `${basePosition + dragPercentage}vh`;
      } else if (isExpanded && dragOffset > 0) {
        // Dragging down from expanded state
        return `${basePosition + dragPercentage}vh`;
      }

      return `${basePosition}vh`;
    }}
  );
  transition: ${({ isDragging }) =>
    isDragging ? "none" : "transform 0.3s ease"};
  overflow: hidden;
  pointer-events: auto;
`;

const BottomSheetHandle = styled.div`
  width: 40px;
  height: 4px;
  background: #d1d5db;
  border-radius: 2px;
  cursor: pointer;
`;

const BottomSheetContent = styled.div<{ isExpanded: boolean }>`
  padding: 0;
  overflow-y: ${({ isExpanded }) => (isExpanded ? "auto" : "hidden")};
  height: calc(100vh + 30px + env(safe-area-inset-top));
  padding-bottom: calc(
    env(safe-area-inset-bottom) + 100px + calc(env(safe-area-inset-top))
  );
  padding-top: 0;
  background: #f7fcf1;
`;

const HandleContainer = styled(Container)`
  align-content: center;
  background-color: white;
  border-radius: 20px 20px 0 0;
  height: 20px;
  justify-content: center;
  width: 100%;
  display: flex;
  align-items: center;
`;

const AddMoreButton = styled(Button)`
  margin-left: auto;
`;
